<script setup lang="ts">
import { computed, watch } from 'vue'

import { useAnomalies } from '../composables/useAnomalies'
import { useAnomalyFormat } from '../composables/useAnomalyFormat'
import type { Anomaly } from '../models/anomaly.types'
import { ANOMALIES_ROUTE } from '../routes'

import { MetricLabel } from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Button } from '@/shared/ui/button'
import { Icon } from '@/shared/ui/icon'
import { PlatformLogo, resolveStaticPlatformLogo } from '@/shared/ui/logo'
import { Skeleton } from '@/shared/ui/skeleton'
import { Tooltip } from '@/shared/ui/tooltip'

const props = withDefaults(
  defineProps<{
    title?: string
    limit?: number
    workspaceIds?: readonly number[]
  }>(),
  { limit: 3 },
)

const emit = defineEmits<{ count: [count: number] }>()

const { t } = useI18n()
const format = useFormat()
const anomalyFormat = useAnomalyFormat()
const anomalyState = useAnomalies(
  props.workspaceIds ? { workspaceIds: () => props.workspaceIds ?? [] } : {},
)
const { state, load } = anomalyState

const visibleAnomalies = computed(() => anomalyState.anomalies.value.slice(0, props.limit))
const count = computed(() => anomalyState.anomalies.value.length)

watch(
  [count, state],
  ([value, loadState]) => {
    if (loadState === 'ready') emit('count', value)
  },
  { immediate: true },
)

function deviation(value: number): string {
  return format.formatPercent(value, { scale: 'percent', maxFrac: 1, signDisplay: 'always' })
}

// Exclusive utility pair per record: two conflicting colour utilities in one class attribute would
// be resolved by their order in the emitted sheet, not by the order they are written here.
function toneClass(anomaly: Anomaly): string {
  return anomalyFormat.isPositiveTrend(anomaly.polarity, anomaly.direction)
    ? 'bg-success-soft text-success'
    : 'bg-danger-soft text-danger'
}

function logoSource(anomaly: Anomaly): string {
  return (
    resolveStaticPlatformLogo(anomaly.platform.iconKey) ??
    resolveStaticPlatformLogo(anomaly.platform.label) ??
    resolveStaticPlatformLogo(anomaly.subSegmentRaw) ??
    ''
  )
}
</script>

<template>
  <section
    v-if="state === 'loading' || state === 'error' || visibleAnomalies.length"
    class="w-full p-4 rounded-md bg-surface shadow-card"
    data-testid="anomalies-teaser"
  >
    <header class="flex items-center justify-between gap-sm min-h-7 mb-md">
      <h2 class="flex items-end gap-sm text-primary text-sm font-semibold tracking-normal">
        {{ title || t('anomalies.teaser.title') }}
        <span
          class="relative z-0 grid size-5 place-items-center rounded-full bg-[image:var(--ui-gradient-brand)] text-primary-fg text-2xs font-bold before:absolute before:-z-1 before:size-7 before:border-2 before:border-accent before:rounded-[inherit] before:content-[''] before:opacity-50"
          >{{ count }}</span
        >
      </h2>
      <RouterLink
        class="flex items-center gap-2xs text-info text-xs font-bold no-underline"
        :to="ANOMALIES_ROUTE"
      >
        {{ t('anomalies.teaser.seeAll') }}
        <Icon name="arrow-right" :size="11" />
      </RouterLink>
    </header>

    <div v-if="state === 'loading'" class="flex flex-col gap-sm" aria-live="polite">
      <div
        v-for="item in limit"
        :key="item"
        class="p-3 border border-border rounded-lg bg-surface flex flex-col gap-xs"
      >
        <Skeleton width="62%" height="0.85rem" />
        <Skeleton width="100%" height="0.75rem" />
        <Skeleton width="78%" height="0.75rem" />
      </div>
    </div>

    <div
      v-else-if="state === 'error'"
      class="flex items-center justify-between gap-sm p-sm rounded-md bg-danger-soft text-danger text-xs"
      role="alert"
    >
      <span>{{ t('anomalies.error.load') }}</span>
      <Button variant="outline" size="xs" @click="load">{{ t('anomalies.error.retry') }}</Button>
    </div>

    <div v-else class="flex flex-col gap-sm">
      <RouterLink
        v-for="anomaly in visibleAnomalies"
        :key="anomaly.id"
        class="anomalies-teaser__card p-3 border border-border rounded-lg bg-surface text-inherit min-h-43 no-underline transition-[border-color,box-shadow] duration-150 ease-initial hover:border-primary-subtle hover:shadow-pop"
        :to="ANOMALIES_ROUTE"
      >
        <div class="flex items-center gap-2xs">
          <span
            class="min-w-0 overflow-hidden text-primary text-sm font-semibold text-ellipsis whitespace-nowrap"
            >{{ anomalyFormat.narrative(anomaly).title }}</span
          >
          <span
            v-if="logoSource(anomaly)"
            class="grid size-5 flex-none place-items-center border border-border rounded-sm"
          >
            <PlatformLogo
              :platform="anomaly.platform.label"
              :src="logoSource(anomaly)"
              :alt="t(anomaly.platform.labelKey)"
              :size="14"
            />
          </span>
          <span
            v-else
            class="anomalies-teaser__dimension-chip max-w-30 overflow-hidden py-0.5 px-1.5 border border-border rounded-sm bg-surface-sunken text-muted text-2xs font-bold text-ellipsis whitespace-nowrap"
            :title="
              t('anomalies.detail.dimension', {
                segment: anomaly.segmentName,
                subSegment: anomaly.subSegmentName,
              })
            "
          >
            {{
              t('anomalies.detail.dimension', {
                segment: anomaly.segmentName,
                subSegment: anomaly.subSegmentName,
              })
            }}
          </span>
          <Icon class="ml-auto text-muted" name="arrow-right" :size="11" />
        </div>

        <div class="flex items-center gap-xs mt-sm text-muted text-xs">
          <span
            class="anomalies-teaser__trend rounded-sm font-bold inline-flex min-w-0 items-center gap-2xs py-0.5 px-1.5"
            :class="toneClass(anomaly)"
          >
            <MetricLabel :field="anomaly.metricField" :size="14" />
            <Icon
              class="flex-none"
              :class="{ '-scale-y-100': anomaly.direction === 'DOWN' }"
              name="chart-line-up"
              :size="11"
            />
          </span>
          <Tooltip :text="`Data day: ${anomaly.startDate}`">
            <span>{{ format.formatRelative(anomaly.startDate || anomaly.createdAt) }}</span>
          </Tooltip>
        </div>

        <p class="line-clamp-2 my-3 text-muted text-xs leading-5">
          {{ anomalyFormat.narrative(anomaly).description }}
        </p>

        <div class="anomalies-teaser__values flex items-center gap-xl">
          <span class="flex flex-col min-w-0">
            <small class="text-muted text-2xs font-medium tracking-normal uppercase">{{
              t('anomalies.card.expected')
            }}</small>
            <strong class="text-muted text-xs">
              {{ anomalyFormat.formatMetric(anomaly.expectedRange[0], anomaly.metricField) }}
              {{ t('anomalies.card.rangeSeparator') }}
              {{ anomalyFormat.formatMetric(anomaly.expectedRange[1], anomaly.metricField) }}
            </strong>
          </span>
          <span class="flex flex-col min-w-0">
            <small class="text-muted text-2xs font-medium tracking-normal uppercase">{{
              t('anomalies.card.current')
            }}</small>
            <strong class="text-muted text-xs">{{
              anomalyFormat.formatMetric(anomaly.currentValue, anomaly.metricField)
            }}</strong>
          </span>
          <b
            class="ml-auto py-0.5 px-1.5 rounded-sm text-2xs font-bold"
            :class="toneClass(anomaly)"
          >
            {{ deviation(anomaly.deviationPercentage) }}
          </b>
        </div>
      </RouterLink>
    </div>
  </section>
</template>
